import { useCallback } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import { quotationActivityShape, quotationErrorShape } from "app/utils/propTypes";
import QuotationActivityGuest from "app/pages/Booking/Quotation/Activity/QuotationActivityGuest/QuotationActivityGuest";
import QuotationActivityDate from "app/pages/Booking/Quotation/Activity/QuotationActivityDate/QuotationActivityDate";
import { getGuestsCountCrossCategories } from "app/pages/Booking/Quotation/Activity/QuotationActivitySelectors";
import { updateBookingActivityDate } from "app/pages/Booking/bookingActionCreators";
import QuotationActivityHeader from "../QuotationActivityHeader/QuotationActivityHeader";
import get from "lodash/get";
import {
	checkIfActivityIncluded,
	getActivityTotalPrice,
	getActivityTotalDiscount,
} from "app/utils/utils";
import { FormattedMessage } from "react-intl";
import AmountContainer from "app/pages/.shared/AmountContainer";
import { sendTagOnActivityGuestChange } from "app/utils/analytics";

import "./QuotationActivity.scss";
import { useFormikContext } from "formik";

const computeMinGuestPrice = guestsOfDate => {
	if (guestsOfDate) {
		let min = get(guestsOfDate, "[0].pricePerQuantity[1].price", 0);

		guestsOfDate.forEach(guest => {
			const guestPrice = get(guest, "pricePerQuantity[1].price", 0);
			if (guestPrice < min) {
				min = guestPrice;
			}
		});

		return min;
	}

	return -1;
};

const QuotationActivity = ({
	activity,
	guestsCountCrossCategories,
	updateBookingActivityDate,
	onChange,
	error,
	index,
}) => {
	const { values } = useFormikContext();

	const currentActivity = values?.activities?.find(act => act.code === activity.code);

	const selectedDate = currentActivity?.date;

	const guestsForSelectedDate = activity.guestsPerDay ? activity.guestsPerDay[selectedDate] : [];

	const handleGuestChange = useCallback(
		(value, guest) => {
			onChange({
				code: activity.code,
				name: activity.name,
				guest: {
					code: guest.code,
					guestsCount: value,
				},
			});

			sendTagOnActivityGuestChange(activity.name, guest.code, value);
		},
		[activity.code, activity.name]
	);

	const handleDateChange = useCallback(
		value => {
			updateBookingActivityDate(activity.code, value);
		},
		[activity.code]
	);

	const pricingPerParticipant = (guestsForSelectedDate || []).reduce(
		(acc, { code, pricePerQuantity }) => {
			acc[code] = Object.fromEntries(
				Object.entries(pricePerQuantity).map(([quantity, { price = 0, discount = 0 }]) => [
					quantity,
					{ price, discount },
				])
			);
			return acc;
		},
		{}
	);

	const numberOfParticipants = currentActivity?.guests;

	const extraAmount = getActivityTotalPrice(pricingPerParticipant, numberOfParticipants);

	const minGuestPrice = computeMinGuestPrice(guestsForSelectedDate);
	const hasIncludedGuest = checkIfActivityIncluded(guestsForSelectedDate);

	const isActivitySelected = guestsCountCrossCategories > 0;

	const hasDuplicateDate =
		isActivitySelected &&
		error &&
		error.duplicateDates &&
		error.duplicateDates.includes(selectedDate);

	const activityClassName = classNames("quotation-activity", {
		"quotation-activity--selected": isActivitySelected,
		"quotation-activity--duplicate-date": hasDuplicateDate,
	});
	const inputsClassName = classNames("quotation-activity__inputs", {
		"quotation-activity__inputs--selected": isActivitySelected,
	});

	const availableDates = activity.guestsPerDay ? Object.keys(activity.guestsPerDay) : [];

	const totalDiscount = getActivityTotalDiscount(pricingPerParticipant, numberOfParticipants);

	return (
		<div className={activityClassName} data-testid={`quotation-activity-${activity.code}`}>
			<QuotationActivityHeader
				activity={activity}
				hasIncludedGuest={hasIncludedGuest}
				total={extraAmount}
				minGuestPrice={minGuestPrice}
			/>

			<div className={inputsClassName}>
				{guestsForSelectedDate && (
					<QuotationActivityGuest
						index={index}
						guests={guestsForSelectedDate}
						onActivityChange={handleGuestChange}
					/>
				)}

				{totalDiscount < 0 && (
					<div className="quotation-activity__discount-total">
						<FormattedMessage
							id="quotation.activity.discount.amount.label"
							values={{
								discountAmount: <AmountContainer amount={totalDiscount} />,
							}}
						/>
					</div>
				)}

				{availableDates.length > 0 && guestsCountCrossCategories > 0 && (
					<QuotationActivityDate
						index={index}
						hasIncludedGuest={hasIncludedGuest}
						guestsPerDay={activity.guestsPerDay}
						activityCode={activity.code}
						availableDates={availableDates}
						defaultDate={activity.suggestedDate}
						onChange={handleDateChange}
					/>
				)}
			</div>
		</div>
	);
};

QuotationActivity.propTypes = {
	activity: quotationActivityShape,
	guestsCountCrossCategories: PropTypes.number,
	updateBookingActivityDate: PropTypes.func,
	onChange: PropTypes.func,
	error: quotationErrorShape,
	index: PropTypes.number,
};

const mapStateToProps = (state, { activity }) => {
	const activityCode = activity ? activity.code : "";
	return {
		guestsCountCrossCategories: getGuestsCountCrossCategories(activityCode)(state),
	};
};

const mapsDispatchToProps = {
	updateBookingActivityDate,
};

export default connect(
	mapStateToProps,
	mapsDispatchToProps
)(QuotationActivity);
